import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import RecipeMenuGroup from './RecipeMenuGroup';
import RecipeModal from './RecipeModal';
import { hydrateRecipeForModal } from '../utils/recipeModalData';

function clampServingSize(value, fallback = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return Math.max(1, Number(fallback || 1) || 1);
  return Math.max(1, Math.min(12, Math.round(parsed)));
}

function buildRecipeCardModel(suggestion) {
  return {
    id: suggestion?.recipeId,
    recipeId: suggestion?.recipeId,
    name: suggestion?.title,
    title: suggestion?.title,
    imageUrl: suggestion?.imageUrl || suggestion?.image_url || '',
    image_url: suggestion?.image_url || suggestion?.imageUrl || '',
    suggestedPairings: []
  };
}

export default function BreakfastRecommendationModal({ isOpen, onClose, onComplete }) {
  const [servingSize, setServingSize] = useState(1);
  const [confirmingConsume, setConfirmingConsume] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consuming, setConsuming] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);

  const enrichSuggestionsWithImages = useCallback(async (suggestions = []) => {
    const list = Array.isArray(suggestions) ? suggestions : [];
    const enriched = await Promise.all(list.map(async (suggestion) => {
      if (suggestion?.imageUrl || suggestion?.image_url) return suggestion;
      try {
        const hydrated = await hydrateRecipeForModal({
          id: suggestion?.recipeId,
          recipeId: suggestion?.recipeId,
          name: suggestion?.title || 'Kahvalti Onerisi',
          title: suggestion?.title || 'Kahvalti Onerisi'
        });
        return {
          ...suggestion,
          imageUrl: hydrated?.imageUrl || hydrated?.image_url || suggestion?.imageUrl || null,
          image_url: hydrated?.image_url || hydrated?.imageUrl || suggestion?.image_url || null
        };
      } catch (_error) {
        return suggestion;
      }
    }));
    return enriched;
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    const fetchRecommendations = async () => {
      setLoading(true);
      setError('');
      try {
        const authToken = localStorage.getItem('authToken');
        const res = await fetch(
          `${API_BASE_URL}/api/breakfast/recommendations?intentSource=notification_open&surface=breakfast_modal`,
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'breakfast_recommendations_failed');
        }
        if (cancelled) return;

        const hydratedSuggestions = await enrichSuggestionsWithImages(data?.suggestions || []);
        setPayload({
          ...data,
          suggestions: hydratedSuggestions
        });
        const nextServingSize = clampServingSize(data?.defaultServingSize || data?.requestedServingSize || 1, 1);
        setServingSize(nextServingSize);
        setSelectedRecipeId('');
      } catch (_error) {
        if (!cancelled) {
          setError('Kahvalti onerileri su an yuklenemedi.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchRecommendations();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const suggestions = useMemo(
    () => (Array.isArray(payload?.suggestions) ? payload.suggestions : []),
    [payload]
  );
  const selectedSuggestion = suggestions.find((item) => String(item?.recipeId || '').trim() === selectedRecipeId) || null;
  const selectedRecipeNames = selectedSuggestion?.title ? [selectedSuggestion.title] : [];

  const handleToggleSuggestion = useCallback((suggestion) => {
    const recipeId = String(suggestion?.recipeId || '').trim();
    if (!recipeId) return;
    setSelectedRecipeId(recipeId);
  }, []);

  const handleOpenRecipe = useCallback(async (suggestion) => {
    const recipeId = String(suggestion?.recipeId || '').trim();
    if (!recipeId) return;
    const hydrated = await hydrateRecipeForModal({
      id: recipeId,
      recipeId,
      name: suggestion?.title || 'Kahvalti Onerisi',
      title: suggestion?.title || 'Kahvalti Onerisi',
      imageUrl: suggestion?.imageUrl || suggestion?.image_url || null,
      image_url: suggestion?.image_url || suggestion?.imageUrl || null
    });
    setSelectedRecipe(hydrated);
    setRecipeModalOpen(true);
  }, []);

  const handleRecipeModalMarkAsUsed = useCallback(async (recipe) => {
    const recipeId = String(recipe?.recipeId || recipe?.id || '').trim();
    if (!recipeId) return;
    setSelectedRecipeId(recipeId);
    setRecipeModalOpen(false);
    setConfirmingConsume(true);
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setError('');
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/breakfast/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          intentSource: 'modal_refresh',
          surface: 'breakfast_modal',
          servingSize
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'breakfast_refresh_failed');
      }
      const hydratedSuggestions = await enrichSuggestionsWithImages(data?.suggestions || []);
      setPayload({
        ...data,
        suggestions: hydratedSuggestions
      });
      setSelectedRecipeId('');
    } catch (_error) {
      setError('Yeni kahvalti onerileri su an alinamadi.');
    } finally {
      setLoading(false);
    }
  };

  const handleConsume = async (confirmedServingSize = servingSize) => {
    if (!selectedSuggestion || !payload?.breakfastRecommendationFingerprint) return;

    setConsuming(true);
    setError('');
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/breakfast/consume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          recipeId: selectedSuggestion.recipeId,
          breakfastRecommendationFingerprint: payload.breakfastRecommendationFingerprint,
          sourceSurface: 'breakfast_modal',
          consumeSidePack: selectedSuggestion?.sidePack?.enabled === true,
          sidePackIngredientIds: Array.isArray(selectedSuggestion?.sidePack?.items)
            ? selectedSuggestion.sidePack.items.map((item) => item.ingredientId).filter(Boolean)
            : [],
          servingSize: clampServingSize(confirmedServingSize, servingSize)
        })
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || 'breakfast_consume_failed');
      }
      setRecipeModalOpen(false);
      setSelectedRecipe(null);
      if (typeof onComplete === 'function') {
        onComplete(data);
      }
    } catch (_error) {
      setError('Kahvalti tuketimi kaydedilemedi.');
    } finally {
      setConsuming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[320] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-6 dark:border-slate-800">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">
              <span className="material-symbols-outlined text-3xl">breakfast_dining</span>
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">Gunaydin Sefim!</h3>
              <p className="max-w-2xl text-sm font-medium leading-6 text-slate-600 dark:text-slate-300">
                Bugunku kahvalti onerileriniz. Oneriler evdeki malzemelere gore hazirlandi. Afiyet olsun!
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mb-6 rounded-3xl border border-amber-100 bg-amber-50 px-5 py-4 dark:border-amber-500/10 dark:bg-amber-500/5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-600 dark:text-amber-300">Sabah Tuketimi</p>
                <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Bu sabah kahvaltiyi kac kisi tuketecek?</p>
              </div>
              <div className="flex items-center gap-2 rounded-2xl bg-white p-2 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900 dark:ring-slate-800">
                <button
                  onClick={() => setServingSize((prev) => clampServingSize(prev - 1, 1))}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <span className="material-symbols-outlined">remove</span>
                </button>
                <div className="min-w-[72px] text-center">
                  <div className="text-2xl font-black text-slate-900 dark:text-white">{servingSize}</div>
                  <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Kisi</div>
                </div>
                <button
                  onClick={() => setServingSize((prev) => clampServingSize(prev + 1, prev))}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <span className="material-symbols-outlined">add</span>
                </button>
              </div>
            </div>
          </div>

          {error ? (
            <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-amber-500" />
            </div>
          ) : payload?.status === 'completed' ? (
            <div className="rounded-[2rem] border border-emerald-200 bg-emerald-50 px-6 py-8 text-center dark:border-emerald-500/20 dark:bg-emerald-500/10">
              <p className="text-xl font-black text-emerald-700 dark:text-emerald-300">Bugunku kahvaltini tamamladin.</p>
              <p className="mt-2 text-sm font-medium text-emerald-600 dark:text-emerald-200">Yeni oneri yerine ogle ve aksam akisiniz devam edecek.</p>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="rounded-[2rem] border border-slate-200 bg-slate-50 px-6 py-8 text-center dark:border-slate-800 dark:bg-slate-900">
              <p className="text-lg font-black text-slate-900 dark:text-white">Bu sabah icin uygun kahvalti onerisi bulunamadi.</p>
              <p className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-400">Stokta uygun urunler arttiginda burada yeni oneriler goreceksin.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {suggestions.map((suggestion, index) => (
                <div key={String(suggestion.recipeId || index)} className="space-y-3">
                  <div className="flex items-center justify-between px-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.24em] text-amber-600 dark:text-amber-300">Kahvalti Onerisi {index + 1}</p>
                    {String(suggestion.recipeId || '') === selectedRecipeId ? (
                      <span className="material-symbols-outlined text-amber-500">check_circle</span>
                    ) : null}
                  </div>

                  <RecipeMenuGroup
                    recipe={buildRecipeCardModel(suggestion)}
                    onCardClick={() => handleOpenRecipe(suggestion)}
                    onToggleSelect={() => handleToggleSuggestion(suggestion)}
                    selectedRecipeNames={selectedRecipeNames}
                    hideActionButtons={false}
                    isDarkMode={false}
                    isModern={true}
                    isCompleted={false}
                    layoutMode="featured"
                    userPortion={servingSize}
                  />

                  <div className="rounded-2xl bg-slate-50 px-4 py-4 dark:bg-slate-800/60">
                    <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Kahvalti Tabagin</p>
                    {suggestion?.sidePack?.enabled && Array.isArray(suggestion?.sidePack?.items) && suggestion.sidePack.items.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {suggestion.sidePack.items.map((item) => (
                          <span
                            key={`${suggestion.recipeId}-${item.ingredientId}`}
                            className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                          >
                            {item.title}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm font-medium text-slate-500 dark:text-slate-400">Bugunku stokta uygun yan tabak gorunmuyor.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={handleRefresh}
              disabled={loading || consuming || suggestions.length === 0}
              className="flex-1 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-black uppercase tracking-[0.2em] text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Baska Oner
            </button>
            <button
              onClick={() => setConfirmingConsume(true)}
              disabled={!selectedSuggestion || consuming || loading}
              className="flex-1 rounded-2xl bg-amber-500 px-5 py-4 text-sm font-black uppercase tracking-[0.2em] text-white shadow-xl shadow-amber-500/20 transition hover:bg-amber-600 disabled:opacity-50"
            >
              {consuming ? 'Kaydediliyor' : 'Yedim'}
            </button>
          </div>
        </div>
      </div>

      {confirmingConsume ? (
        <div className="absolute inset-0 z-[330] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[1.8rem] border border-white/10 bg-white p-6 shadow-2xl dark:bg-slate-950">
            <div className="space-y-2">
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-600 dark:text-amber-300">Son Kontrol</p>
              <h4 className="text-xl font-black text-slate-900 dark:text-white">Sefim, bu kahvaltiyi kac kisi tuketti?</h4>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                Varsayilan oneri: {servingSize} kisi. Stok bu sayi uzerinden dusecek.
              </p>
            </div>

            <div className="mt-6 flex items-center justify-center gap-3 rounded-2xl bg-amber-50 p-3 dark:bg-amber-500/10">
              <button
                onClick={() => setServingSize((prev) => clampServingSize(prev - 1, 1))}
                className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <span className="material-symbols-outlined">remove</span>
              </button>
              <div className="min-w-[84px] text-center">
                <div className="text-3xl font-black text-slate-900 dark:text-white">{servingSize}</div>
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Kisi</div>
              </div>
              <button
                onClick={() => setServingSize((prev) => clampServingSize(prev + 1, prev))}
                className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <span className="material-symbols-outlined">add</span>
              </button>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmingConsume(false)}
                className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Vazgec
              </button>
              <button
                type="button"
                onClick={async () => {
                  setConfirmingConsume(false);
                  await handleConsume(servingSize);
                }}
                className="flex-1 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-black text-white shadow-xl shadow-amber-500/20 transition hover:bg-amber-600"
              >
                Kaydet ve stogu dus
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedRecipe && (
        <RecipeModal
          isOpen={recipeModalOpen}
          onClose={() => setRecipeModalOpen(false)}
          recipe={selectedRecipe}
          onMarkAsUsed={handleRecipeModalMarkAsUsed}
          onPairingClick={() => null}
          userProfile={null}
          userPortion={servingSize}
          skillLevel={null}
          isDarkMode={false}
        />
      )}
    </div>
  );
}
